import React, { useState } from 'react';
import { 
  Pi, 
  Plus,
  Minus,
  X,
  Divide,
  Infinity,
  Calculator,
  Type,
  Image as ImageIcon,
  Triangle,
  Circle,
  Square as SquareIcon,
  FileText,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

/**
 * Reusable Math Toolbar Component
 * Extracted from matheditor with enhanced functionality
 */
const MathToolbar = ({ 
  onInsert, 
  className = '',
  isCollapsed = true,
  onToggleCollapse 
}) => {
  const [showSymbols, setShowSymbols] = useState(!isCollapsed);
  const [showFractionEditor, setShowFractionEditor] = useState(false);
  const [showTrigHelper, setShowTrigHelper] = useState(false);
  const [showDiagramEditor, setShowDiagramEditor] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  const insertAtCursor = (text) => {
    if (onInsert) {
      onInsert(text);
    } else {
    }
  };

  const insertMathExpression = (expression) => {
    insertAtCursor(`$${expression}$`);
  };

  const insertDisplayMath = (expression) => {
    insertAtCursor(`$$${expression}$$`);
  };

  const insertSymbol = (latex) => {
    insertAtCursor(latex);
    setShowSymbols(false);
  };

  const insertTrigFunction = (latex) => {
    insertAtCursor(latex + '()');
    setShowTrigHelper(false);
  };

  const insertExpression = (latex) => {
    insertAtCursor(latex);
  };

  const handleFractionSubmit = (numerator, denominator) => {
    const fractionLatex = `\\frac{${numerator}}{${denominator}}`;
    insertAtCursor(fractionLatex);
    setShowFractionEditor(false);
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target.result;
        const imageLatex = `\\includegraphics[width=0.5\\textwidth]{${imageUrl}}`;
        insertAtCursor(imageLatex);
      };
      reader.readAsDataURL(file);
    });
  };

  const insertTable = (rows, cols) => {
    let table = '\\begin{array}{|';
    for (let i = 0; i < cols; i++) {
      table += 'c|';
    }
    table += '}';
    
    // Header row
    const headers = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
    table += '\\hline';
    for (let j = 0; j < cols; j++) {
      table += `\\text{${headers[j] || `Col ${j+1}`}}`;
      if (j < cols - 1) table += ' & ';
    }
    table += ' \\\\ \\hline';
    
    // Data rows
    for (let i = 1; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        table += `${i},${j+1}`;
        if (j < cols - 1) table += ' & ';
      }
      if (i < rows - 1) table += ' \\\\ \\hline';
    }
    table += ' \\\\ \\hline \\end{array}';
    insertDisplayMath(table);
  };

  // Mathematical symbols organized by category
  const mathSymbols = {
    basic: [
      { symbol: '+', latex: '+' },
      { symbol: '−', latex: '-' },
      { symbol: '×', latex: '\\times' },
      { symbol: '÷', latex: '\\div' },
      { symbol: '=', latex: '=' },
      { symbol: '≠', latex: '\\neq' },
      { symbol: '<', latex: '<' },
      { symbol: '>', latex: '>' },
      { symbol: '≤', latex: '\\leq' },
      { symbol: '≥', latex: '\\geq' },
      { symbol: '±', latex: '\\pm' },
      { symbol: '°', latex: '^\\circ', title: 'Degree symbol' },
      { symbol: '∞', icon: <Infinity size={16} />, latex: '\\infty' }
    ],
    fractions: [
      { symbol: '½', latex: '\\frac{1}{2}' },
      { symbol: '⅓', latex: '\\frac{1}{3}' },
      { symbol: '¼', latex: '\\frac{1}{4}' },
      { symbol: '⅕', latex: '\\frac{1}{5}' },
      { symbol: '⅙', latex: '\\frac{1}{6}' },
      { symbol: '⅐', latex: '\\frac{1}{7}' },
      { symbol: '⅛', latex: '\\frac{1}{8}' },
      { symbol: '⅑', latex: '\\frac{1}{9}' },
      { symbol: '⅒', latex: '\\frac{1}{10}' }
    ],
    powers: [
      { symbol: 'x²', latex: 'x^2' },
      { symbol: 'x³', latex: 'x^3' },
      { symbol: 'xⁿ', latex: 'x^n' },
      { symbol: '√', latex: '\\sqrt{}' },
      { symbol: '∛', latex: '\\sqrt[3]{}' },
      { symbol: '∜', latex: '\\sqrt[4]{}' }
    ],
    greek: [
      { symbol: 'α', latex: '\\alpha' },
      { symbol: 'β', latex: '\\beta' },
      { symbol: 'γ', latex: '\\gamma' },
      { symbol: 'δ', latex: '\\delta' },
      { symbol: 'ε', latex: '\\varepsilon' },
      { symbol: 'θ', latex: '\\theta' },
      { symbol: 'λ', latex: '\\lambda' },
      { symbol: 'μ', latex: '\\mu' },
      { symbol: 'π', icon: <Pi size={16} />, latex: '\\pi' },
      { symbol: 'σ', latex: '\\sigma' },
      { symbol: 'φ', latex: '\\phi' },
      { symbol: 'ω', latex: '\\omega' }
    ],
    calculus: [
      { symbol: '∫', latex: '\\int' },
      { symbol: '∂', latex: '\\partial' },
      { symbol: '∑', latex: '\\sum' },
      { symbol: '∏', latex: '\\prod' },
      { symbol: 'lim', latex: '\\lim' },
      { symbol: 'dx', latex: 'dx' },
      { symbol: 'dy', latex: 'dy' },
      { symbol: 'dz', latex: 'dz' }
    ],
    sets: [
      { symbol: '∈', latex: '\\in' },
      { symbol: '∉', latex: '\\notin' },
      { symbol: '⊂', latex: '\\subset' },
      { symbol: '⊃', latex: '\\supset' },
      { symbol: '∪', latex: '\\cup' },
      { symbol: '∩', latex: '\\cap' },
      { symbol: '∅', latex: '\\emptyset' },
      { symbol: 'ℝ', latex: '\\mathbb{R}' },
      { symbol: 'ℤ', latex: '\\mathbb{Z}' },
      { symbol: 'ℕ', latex: '\\mathbb{N}' },
      { symbol: 'ℚ', latex: '\\mathbb{Q}' }
    ]
  };

  // Trigonometry functions
  const trigFunctions = [
    { name: 'sin', latex: '\\sin' },
    { name: 'cos', latex: '\\cos' },
    { name: 'tan', latex: '\\tan' },
    { name: 'csc', latex: '\\csc' },
    { name: 'sec', latex: '\\sec' },
    { name: 'cot', latex: '\\cot' },
    { name: 'arcsin', latex: '\\arcsin' },
    { name: 'arccos', latex: '\\arccos' },
    { name: 'arctan', latex: '\\arctan' }
  ];

  // Common mathematical expressions
  const commonExpressions = [
    { name: 'Quadratic Formula', latex: 'x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}' },
    { name: 'Pythagorean Theorem', latex: 'a^2 + b^2 = c^2' },
    { name: 'Area of Circle', latex: 'A = \\pi r^2' },
    { name: 'Circumference', latex: 'C = 2\\pi r' },
    { name: 'Slope Formula', latex: 'm = \\frac{y_2 - y_1}{x_2 - x_1}' },
    { name: 'Distance Formula', latex: 'd = \\sqrt{(x_2 - x_1)^2 + (y_2 - y_1)^2}' },
    { name: 'Binomial Theorem', latex: '(a + b)^n = \\sum_{k=0}^{n} \\binom{n}{k} a^{n-k} b^k' }
  ];

  const toolbarButtons = [
    // Basic operations
    {
      group: 'Basic',
      buttons: [
        { label: 'Fraction', action: () => insertMathExpression('\\frac{a}{b}') },
        { label: 'Power', action: () => insertMathExpression('x^{2}') },
        { label: 'Subscript', action: () => insertMathExpression('x_{1}') },
        { label: 'Square Root', action: () => insertMathExpression('\\sqrt{x}') },
        { label: 'nth Root', action: () => insertMathExpression('\\sqrt[n]{x}') },
        { label: 'Absolute', action: () => insertMathExpression('|x|') },
        { label: 'Floor', action: () => insertMathExpression('\\lfloor x \\rfloor') },
        { label: 'Ceiling', action: () => insertMathExpression('\\lceil x \\rceil') },
        { label: 'GCD', action: () => insertMathExpression('\\gcd(a,b)') },
        { label: 'LCM', action: () => insertMathExpression('\\text{lcm}(a,b)') },
        { label: 'Mod', action: () => insertMathExpression('a \\bmod b') }
      ]
    },
    // Greek letters
    {
      group: 'Greek',
      buttons: [
        { label: 'α', action: () => insertMathExpression('\\alpha') },
        { label: 'β', action: () => insertMathExpression('\\beta') },
        { label: 'γ', action: () => insertMathExpression('\\gamma') },
        { label: 'δ', action: () => insertMathExpression('\\delta') },
        { label: 'ε', action: () => insertMathExpression('\\varepsilon') },
        { label: 'ζ', action: () => insertMathExpression('\\zeta') },
        { label: 'η', action: () => insertMathExpression('\\eta') },
        { label: 'θ', action: () => insertMathExpression('\\theta') },
        { label: 'ι', action: () => insertMathExpression('\\iota') },
        { label: 'κ', action: () => insertMathExpression('\\kappa') },
        { label: 'λ', action: () => insertMathExpression('\\lambda') },
        { label: 'μ', action: () => insertMathExpression('\\mu') },
        { label: 'ν', action: () => insertMathExpression('\\nu') },
        { label: 'ξ', action: () => insertMathExpression('\\xi') },
        { label: 'π', icon: <Pi size={16} />, action: () => insertMathExpression('\\pi') },
        { label: 'ρ', action: () => insertMathExpression('\\rho') },
        { label: 'σ', action: () => insertMathExpression('\\sigma') },
        { label: 'τ', action: () => insertMathExpression('\\tau') },
        { label: 'υ', action: () => insertMathExpression('\\upsilon') },
        { label: 'φ', action: () => insertMathExpression('\\phi') },
        { label: 'χ', action: () => insertMathExpression('\\chi') },
        { label: 'ψ', action: () => insertMathExpression('\\psi') },
        { label: 'ω', action: () => insertMathExpression('\\omega') }
      ]
    },
    // Chemistry - Elements
    {
      group: 'Chemistry',
      buttons: [
        { label: 'H₂O', action: () => insertMathExpression('\\text{H}_2\\text{O}') },
        { label: 'CO₂', action: () => insertMathExpression('\\text{CO}_2') },
        { label: 'CH₄', action: () => insertMathExpression('\\text{CH}_4') },
        { label: 'NH₃', action: () => insertMathExpression('\\text{NH}_3') },
        { label: 'H₂SO₄', action: () => insertMathExpression('\\text{H}_2\\text{SO}_4') },
        { label: 'NaOH', action: () => insertMathExpression('\\text{NaOH}') },
        { label: 'HCl', action: () => insertMathExpression('\\text{HCl}') },
        { label: 'CaCO₃', action: () => insertMathExpression('\\text{CaCO}_3') },
        { label: '→', action: () => insertMathExpression('\\rightarrow') },
        { label: '⇌', action: () => insertMathExpression('\\rightleftharpoons') },
        { label: '↑', action: () => insertMathExpression('\\uparrow') },
        { label: '↓', action: () => insertMathExpression('\\downarrow') },
        { label: 'Δ', action: () => insertMathExpression('\\Delta') },
        { label: 'ΔH', action: () => insertMathExpression('\\Delta H') },
        { label: 'ΔG', action: () => insertMathExpression('\\Delta G') },
        { label: 'ΔS', action: () => insertMathExpression('\\Delta S') }
      ]
    },
    // Catalysts
    {
      group: 'Catalysts',
      buttons: [
        { label: '→[cat]', action: () => insertMathExpression('\\xrightarrow{\\text{cat}}') },
        { label: '→[H⁺]', action: () => insertMathExpression('\\xrightarrow{\\text{H}^+}') },
        { label: '→[Δ]', action: () => insertMathExpression('\\xrightarrow{\\Delta}') },
        { label: '→[hv]', action: () => insertMathExpression('\\xrightarrow{h\\nu}') },
        { label: '→[Pt]', action: () => insertMathExpression('\\xrightarrow{\\text{Pt}}') },
        { label: '→[Ni]', action: () => insertMathExpression('\\xrightarrow{\\text{Ni}}') },
        { label: '→[Pd]', action: () => insertMathExpression('\\xrightarrow{\\text{Pd}}') },
        { label: '→[Cu]', action: () => insertMathExpression('\\xrightarrow{\\text{Cu}}') },
        { label: '→[Zn]', action: () => insertMathExpression('\\xrightarrow{\\text{Zn}}') },
        { label: '→[Fe]', action: () => insertMathExpression('\\xrightarrow{\\text{Fe}}') },
        { label: '→[Al]', action: () => insertMathExpression('\\xrightarrow{\\text{Al}}') },
        { label: '→[Mg]', action: () => insertMathExpression('\\xrightarrow{\\text{Mg}}') },
        { label: '→[Ag]', action: () => insertMathExpression('\\xrightarrow{\\text{Ag}}') },
        { label: '→[Au]', action: () => insertMathExpression('\\xrightarrow{\\text{Au}}') },
        { label: '→[Cr]', action: () => insertMathExpression('\\xrightarrow{\\text{Cr}}') },
        { label: '→[Ti]', action: () => insertMathExpression('\\xrightarrow{\\text{Ti}}') }
      ]
    },
    // Physics
    {
      group: 'Physics',
      buttons: [
        { label: 'F=ma', action: () => insertMathExpression('F = ma') },
        { label: 'E=mc²', action: () => insertMathExpression('E = mc^2') },
        { label: 'F=Gm₁m₂/r²', action: () => insertMathExpression('F = G\\frac{m_1 m_2}{r^2}') },
        { label: 'E=½mv²', action: () => insertMathExpression('E = \\frac{1}{2}mv^2') },
        { label: 'p=mv', action: () => insertMathExpression('p = mv') },
        { label: 'W=Fd', action: () => insertMathExpression('W = Fd') },
        { label: 'P=W/t', action: () => insertMathExpression('P = \\frac{W}{t}') },
        { label: 'v=v₀+at', action: () => insertMathExpression('v = v_0 + at') },
        { label: 'F=kx', action: () => insertMathExpression('F = kx') },
        { label: 'T=2π√(m/k)', action: () => insertMathExpression('T = 2\\pi\\sqrt{\\frac{m}{k}}') },
        { label: 'λ=v/f', action: () => insertMathExpression('\\lambda = \\frac{v}{f}') },
        { label: 'E=hf', action: () => insertMathExpression('E = hf') }
      ]
    },
    // Biology
    {
      group: 'Biology',
      buttons: [
        { label: 'DNA', action: () => insertMathExpression('\\text{DNA}') },
        { label: 'RNA', action: () => insertMathExpression('\\text{RNA}') },
        { label: 'ATP', action: () => insertMathExpression('\\text{ATP}') },
        { label: 'ADP', action: () => insertMathExpression('\\text{ADP}') },
        { label: 'NAD⁺', action: () => insertMathExpression('\\text{NAD}^+') },
        { label: 'NADH', action: () => insertMathExpression('\\text{NADH}') },
        { label: 'pH', action: () => insertMathExpression('\\text{pH}') },
        { label: 'Kₐ', action: () => insertMathExpression('K_a') },
        { label: 'Kₑᵩ', action: () => insertMathExpression('K_{eq}') },
        { label: 'μ', action: () => insertMathExpression('\\mu') },
        { label: 'σ', action: () => insertMathExpression('\\sigma') },
        { label: 'χ²', action: () => insertMathExpression('\\chi^2') }
      ]
    },
    // Organic Chemistry
    {
      group: 'Organic',
      buttons: [
        { label: 'Benzene', action: () => insertMathExpression('\\text{C}_6\\text{H}_6') },
        { label: 'Methanol', action: () => insertMathExpression('\\text{CH}_3\\text{OH}') },
        { label: 'Ethanol', action: () => insertMathExpression('\\text{C}_2\\text{H}_5\\text{OH}') },
        { label: 'Acetone', action: () => insertMathExpression('\\text{CH}_3\\text{COCH}_3') },
        { label: 'Glucose', action: () => insertMathExpression('\\text{C}_6\\text{H}_{12}\\text{O}_6') },
        { label: 'Alkane', action: () => insertMathExpression('\\text{C}_n\\text{H}_{2n+2}') },
        { label: 'Alkene', action: () => insertMathExpression('\\text{C}_n\\text{H}_{2n}') },
        { label: 'Alkyne', action: () => insertMathExpression('\\text{C}_n\\text{H}_{2n-2}') },
        { label: 'Alcohol', action: () => insertMathExpression('\\text{R-OH}') },
        { label: 'Aldehyde', action: () => insertMathExpression('\\text{R-CHO}') },
        { label: 'Ketone', action: () => insertMathExpression('\\text{R-CO-R}') },
        { label: 'Carboxylic', action: () => insertMathExpression('\\text{R-COOH}') }
      ]
    },
    // Operators
    {
      group: 'Operators',
      buttons: [
        { label: '±', action: () => insertMathExpression('\\pm') },
        { label: '×', action: () => insertMathExpression('\\times') },
        { label: '÷', action: () => insertMathExpression('\\div') },
        { label: '∞', icon: <Infinity size={16} />, action: () => insertMathExpression('\\infty') },
        { label: '≤', action: () => insertMathExpression('\\leq') },
        { label: '≥', action: () => insertMathExpression('\\geq') },
        { label: '≠', action: () => insertMathExpression('\\neq') },
        { label: '≈', action: () => insertMathExpression('\\approx') },
        { label: '≡', action: () => insertMathExpression('\\equiv') },
        { label: '∝', action: () => insertMathExpression('\\propto') },
        { label: '⊥', action: () => insertMathExpression('\\perp') },
        { label: '∥', action: () => insertMathExpression('\\parallel') },
        { label: '∠', action: () => insertMathExpression('\\angle') },
        { label: '°', action: () => insertMathExpression('^\\circ'), title: 'Degree symbol' },
        { label: '△', action: () => insertMathExpression('\\triangle') },
        { label: '○', action: () => insertMathExpression('\\circ') },
        { label: '□', action: () => insertMathExpression('\\square') }
      ]
    },
    // Calculus
    {
      group: 'Calculus',
      buttons: [
        { label: '∫', action: () => insertMathExpression('\\int') },
        { label: '∫₀ᵗ', action: () => insertMathExpression('\\int_0^t') },
        { label: '∫ᵇₐ', action: () => insertMathExpression('\\int_a^b') },
        { label: '∫∞₀', action: () => insertMathExpression('\\int_0^{\\infty}') },
        { label: '∫∞₋∞', action: () => insertMathExpression('\\int_{-\\infty}^{\\infty}') },
        { label: '∫ᵇₐ f(x)dx', action: () => insertMathExpression('\\int_a^b f(x) \\, dx') },
        { label: '∑', action: () => insertMathExpression('\\sum') },
        { label: '∑ᵢ₌₁ⁿ', action: () => insertMathExpression('\\sum_{i=1}^n') },
        { label: '∏', action: () => insertMathExpression('\\prod') },
        { label: 'lim', action: () => insertMathExpression('\\lim_{x \\to 0}') },
        { label: '∂', action: () => insertMathExpression('\\partial') },
        { label: '∇', action: () => insertMathExpression('\\nabla') },
        { label: '∇f', action: () => insertMathExpression('\\nabla f') },
        { label: '∇·F', action: () => insertMathExpression('\\nabla \\cdot \\vec{F}') },
        { label: '∇×F', action: () => insertMathExpression('\\nabla \\times \\vec{F}') },
        { label: '∇²f', action: () => insertMathExpression('\\nabla^2 f') },
        { label: 'd/dx', action: () => insertMathExpression('\\frac{d}{dx}') },
        { label: 'd²/dx²', action: () => insertMathExpression('\\frac{d^2}{dx^2}') },
        { label: '∫∫', action: () => insertMathExpression('\\iint') },
        { label: '∫∫∫', action: () => insertMathExpression('\\iiint') },
        { label: '∮', action: () => insertMathExpression('\\oint') }
      ]
    },
    // Trigonometry
    {
      group: 'Trig',
      buttons: [
        { label: 'sin', action: () => insertMathExpression('\\sin') },
        { label: 'cos', action: () => insertMathExpression('\\cos') },
        { label: 'tan', action: () => insertMathExpression('\\tan') },
        { label: 'cot', action: () => insertMathExpression('\\cot') },
        { label: 'sec', action: () => insertMathExpression('\\sec') },
        { label: 'csc', action: () => insertMathExpression('\\csc') },
        { label: 'sin⁻¹', action: () => insertMathExpression('\\arcsin') },
        { label: 'cos⁻¹', action: () => insertMathExpression('\\arccos') },
        { label: 'tan⁻¹', action: () => insertMathExpression('\\arctan') },
        { label: 'sinh', action: () => insertMathExpression('\\sinh') },
        { label: 'cosh', action: () => insertMathExpression('\\cosh') },
        { label: 'tanh', action: () => insertMathExpression('\\tanh') },
        { label: 'log', action: () => insertMathExpression('\\log') },
        { label: 'ln', action: () => insertMathExpression('\\ln') },
        { label: 'log₁₀', action: () => insertMathExpression('\\log_{10}') },
        { label: 'log₂', action: () => insertMathExpression('\\log_2') },
        { label: 'sinh⁻¹', action: () => insertMathExpression('\\sinh^{-1}') },
        { label: 'cosh⁻¹', action: () => insertMathExpression('\\cosh^{-1}') },
        { label: 'tanh⁻¹', action: () => insertMathExpression('\\tanh^{-1}') }
      ]
    },
    // Vectors and matrices
    {
      group: 'Vectors',
      buttons: [
        { label: '→', action: () => insertMathExpression('\\vec{a}') },
        { label: '|a|', action: () => insertMathExpression('|\\vec{a}|') },
        { label: 'a·b', action: () => insertMathExpression('\\vec{a} \\cdot \\vec{b}') },
        { label: 'a×b', action: () => insertMathExpression('\\vec{a} \\times \\vec{b}') },
        { label: 'Matrix 2×2', action: () => insertDisplayMath('\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix}') },
        { label: 'Matrix 3×3', action: () => insertDisplayMath('\\begin{pmatrix} a & b & c \\\\ d & e & f \\\\ g & h & i \\end{pmatrix}') },
        { label: 'Det 2×2', action: () => insertDisplayMath('\\begin{vmatrix} a & b \\\\ c & d \\end{vmatrix}') },
        { label: 'Det 3×3', action: () => insertDisplayMath('\\begin{vmatrix} a & b & c \\\\ d & e & f \\\\ g & h & i \\end{vmatrix}') },
        { label: 'Det 4×4', action: () => insertDisplayMath('\\begin{vmatrix} a & b & c & d \\\\ e & f & g & h \\\\ i & j & k & l \\\\ m & n & o & p \\end{vmatrix}') },
        { label: 'A⁻¹', action: () => insertMathExpression('A^{-1}') },
        { label: 'adj(A)', action: () => insertMathExpression('\\text{adj}(A)') },
        { label: 'A⁻¹ = adj(A)/det(A)', action: () => insertMathExpression('A^{-1} = \\frac{\\text{adj}(A)}{\\det(A)}') },
        { label: 'Unit Vector', action: () => insertMathExpression('\\hat{i}, \\hat{j}, \\hat{k}') }
      ]
    },
    // Advanced Math
    {
      group: 'Advanced',
      buttons: [
        { label: 'x̄', action: () => insertMathExpression('\\bar{x}') },
        { label: 'σ', action: () => insertMathExpression('\\sigma') },
        { label: 'μ', action: () => insertMathExpression('\\mu') },
        { label: 'i', action: () => insertMathExpression('i') },
        { label: '|z|', action: () => insertMathExpression('|z|') },
        { label: 'A', action: () => insertMathExpression('A') },
        { label: 'V', action: () => insertMathExpression('V') },
        { label: 'gcd(a,b)=1', action: () => insertMathExpression('\\gcd(a,b) = 1') },
        { label: 'a≡b(mod n)', action: () => insertMathExpression('a \\equiv b \\pmod{n}') },
        { label: 'φ(n)', action: () => insertMathExpression('\\phi(n)') },
        { label: 'ζ(s)', action: () => insertMathExpression('\\zeta(s)') },
        { label: 'e^(iπ)', action: () => insertMathExpression('e^{i\\pi}') },
        { label: 'ω', action: () => insertMathExpression('\\omega') },
        { label: 'ω²', action: () => insertMathExpression('\\omega^2') },
        { label: 'De Moivre', action: () => insertMathExpression('(\\cos\\theta + i\\sin\\theta)^n = \\cos(n\\theta) + i\\sin(n\\theta)') }
      ]
    },
    // JEE Coordinate Geometry
    {
      group: 'Coordinate',
      buttons: [
        { label: 'Circle', action: () => insertMathExpression('x^2 + y^2 = r^2') },
        { label: 'Ellipse', action: () => insertMathExpression('\\frac{x^2}{a^2} + \\frac{y^2}{b^2} = 1') },
        { label: 'Hyperbola', action: () => insertMathExpression('\\frac{x^2}{a^2} - \\frac{y^2}{b^2} = 1') },
        { label: 'Parabola', action: () => insertMathExpression('y^2 = 4ax') },
        { label: 'e', action: () => insertMathExpression('e') },
        { label: 'F₁, F₂', action: () => insertMathExpression('F_1, F_2') },
        { label: 'Directrix', action: () => insertMathExpression('d') },
        { label: 'Distance', action: () => insertMathExpression('d = \\sqrt{(x_2-x_1)^2 + (y_2-y_1)^2}') },
        { label: 'Slope', action: () => insertMathExpression('m = \\frac{y_2-y_1}{x_2-x_1}') },
        { label: 'Midpoint', action: () => insertMathExpression('(\\frac{x_1+x_2}{2}, \\frac{y_1+y_2}{2})') },
        { label: 'Area', action: () => insertMathExpression('A = \\frac{1}{2}|x_1(y_2-y_3) + x_2(y_3-y_1) + x_3(y_1-y_2)|') },
        { label: 'Angle', action: () => insertMathExpression('\\tan\\theta = \\frac{m_2-m_1}{1+m_1m_2}') }
      ]
    },
    // 3D Geometry
    {
      group: '3D Geometry',
      buttons: [
        { label: '3D Distance', action: () => insertMathExpression('d = \\sqrt{(x_2-x_1)^2 + (y_2-y_1)^2 + (z_2-z_1)^2}') },
        { label: 'Direction cosines', action: () => insertMathExpression('l, m, n') },
        { label: 'Direction ratios', action: () => insertMathExpression('a, b, c') },
        { label: 'Plane', action: () => insertMathExpression('ax + by + cz + d = 0') },
        { label: 'Line 3D', action: () => insertMathExpression('\\frac{x-x_1}{a} = \\frac{y-y_1}{b} = \\frac{z-z_1}{c}') },
        { label: 'Angle between planes', action: () => insertMathExpression('\\cos\\theta = \\frac{a_1a_2 + b_1b_2 + c_1c_2}{\\sqrt{a_1^2+b_1^2+c_1^2}\\sqrt{a_2^2+b_2^2+c_2^2}}') },
        { label: 'Distance point-plane', action: () => insertMathExpression('d = \\frac{|ax_1 + by_1 + cz_1 + d|}{\\sqrt{a^2+b^2+c^2}}') },
        { label: 'Cross product', action: () => insertMathExpression('\\vec{a} \\times \\vec{b} = |\\vec{a}||\\vec{b}|\\sin\\theta\\hat{n}') },
        { label: 'Scalar triple', action: () => insertMathExpression('[\\vec{a} \\vec{b} \\vec{c}]') },
        { label: 'Vector triple', action: () => insertMathExpression('\\vec{a} \\times (\\vec{b} \\times \\vec{c})') }
      ]
    },
    // Sequences & Series
    {
      group: 'Sequences',
      buttons: [
        { label: 'AP', action: () => insertMathExpression('a_n = a + (n-1)d') },
        { label: 'GP', action: () => insertMathExpression('a_n = ar^{n-1}') },
        { label: 'HP', action: () => insertMathExpression('H_n') },
        { label: 'Sum AP', action: () => insertMathExpression('S_n = \\frac{n}{2}[2a + (n-1)d]') },
        { label: 'Sum GP', action: () => insertMathExpression('S_n = \\frac{a(r^n - 1)}{r-1}') },
        { label: 'Sum infinite GP', action: () => insertMathExpression('S_\\infty = \\frac{a}{1-r}') },
        { label: 'AM', action: () => insertMathExpression('\\frac{a+b}{2}') },
        { label: 'GM', action: () => insertMathExpression('\\sqrt{ab}') },
        { label: 'HM', action: () => insertMathExpression('\\frac{2ab}{a+b}') },
        { label: 'AM-GM', action: () => insertMathExpression('\\frac{a+b}{2} \\geq \\sqrt{ab}') },
        { label: 'Arithmetic mean', action: () => insertMathExpression('\\bar{x} = \\frac{x_1 + x_2 + ... + x_n}{n}') },
        { label: 'Geometric mean', action: () => insertMathExpression('G = \\sqrt[n]{x_1 \\cdot x_2 \\cdot ... \\cdot x_n}') }
      ]
    },
    // Probability & Statistics
    {
      group: 'Probability',
      buttons: [
        { label: 'P(A)', action: () => insertMathExpression('P(A)') },
        { label: 'P(A∪B)', action: () => insertMathExpression('P(A \\cup B) = P(A) + P(B) - P(A \\cap B)') },
        { label: 'P(A∩B)', action: () => insertMathExpression('P(A \\cap B)') },
        { label: 'P(A|B)', action: () => insertMathExpression('P(A|B) = \\frac{P(A \\cap B)}{P(B)}') },
        { label: 'Bayes', action: () => insertMathExpression('P(A_i|B) = \\frac{P(B|A_i)P(A_i)}{\\sum P(B|A_j)P(A_j)}') },
        { label: 'Binomial', action: () => insertMathExpression('B(n,p)') },
        { label: 'Normal', action: () => insertMathExpression('N(\\mu,\\sigma^2)') },
        { label: 'Poisson', action: () => insertMathExpression('P(\\lambda)') },
        { label: 'E(X)', action: () => insertMathExpression('E(X)') },
        { label: 'Var(X)', action: () => insertMathExpression('\\text{Var}(X)') },
        { label: 'SD(X)', action: () => insertMathExpression('\\text{SD}(X)') },
        { label: 'Cov(X,Y)', action: () => insertMathExpression('\\text{Cov}(X,Y)') }
      ]
    },
    // Limits & Continuity
    {
      group: 'Limits',
      buttons: [
        { label: 'lim⁻', action: () => insertMathExpression('\\lim_{x \\to a^-} f(x)') },
        { label: 'lim⁺', action: () => insertMathExpression('\\lim_{x \\to a^+} f(x)') },
        { label: 'Continuity', action: () => insertMathExpression('f(a^-) = f(a^+) = f(a)') },
        { label: 'L\'Hôpital', action: () => insertMathExpression('\\lim_{x \\to a} \\frac{f(x)}{g(x)} = \\lim_{x \\to a} \\frac{f\'(x)}{g\'(x)}') },
        { label: 'ε-δ', action: () => insertMathExpression('|f(x) - L| < \\varepsilon') },
        { label: 'Squeeze', action: () => insertMathExpression('g(x) \\leq f(x) \\leq h(x)') },
        { label: 'Sandwich', action: () => insertMathExpression('\\lim_{x \\to a} g(x) = \\lim_{x \\to a} h(x) = L') },
        { label: 'Indeterminate', action: () => insertMathExpression('\\frac{0}{0}, \\frac{\\infty}{\\infty}') },
        { label: 'One-sided', action: () => insertMathExpression('\\lim_{x \\to 0^+} \\frac{1}{x} = \\infty') },
        { label: 'Infinite limit', action: () => insertMathExpression('\\lim_{x \\to \\infty} f(x)') }
      ]
    },
    // Differential Equations
    {
      group: 'Diff Eq',
      buttons: [
        { label: 'Order', action: () => insertMathExpression('n') },
        { label: 'Degree', action: () => insertMathExpression('m') },
        { label: 'Homogeneous', action: () => insertMathExpression('\\frac{dy}{dx} = f(\\frac{y}{x})') },
        { label: 'Linear', action: () => insertMathExpression('\\frac{dy}{dx} + Py = Q') },
        { label: 'Bernoulli', action: () => insertMathExpression('\\frac{dy}{dx} + Py = Qy^n') },
        { label: 'Exact', action: () => insertMathExpression('M dx + N dy = 0') },
        { label: 'Separable', action: () => insertMathExpression('\\frac{dy}{dx} = f(x)g(y)') },
        { label: 'Integrating factor', action: () => insertMathExpression('e^{\\int P dx}') },
        { label: 'General solution', action: () => insertMathExpression('y = C e^{\\int P dx}') },
        { label: 'Particular solution', action: () => insertMathExpression('y_p') },
        { label: 'Complementary', action: () => insertMathExpression('y_c') },
        { label: 'Wronskian', action: () => insertMathExpression('W(y_1, y_2)') }
      ]
    },
    // Binomial Theorem
    {
      group: 'Binomial',
      buttons: [
        { label: 'General term', action: () => insertMathExpression('T_{r+1} = \\binom{n}{r} a^{n-r} b^r') },
        { label: 'Middle term', action: () => insertMathExpression('T_{\\frac{n+1}{2}}') },
        { label: 'Coefficient', action: () => insertMathExpression('C_r') },
        { label: 'Sum coefficients', action: () => insertMathExpression('\\sum_{r=0}^n \\binom{n}{r} = 2^n') },
        { label: 'Alternating sum', action: () => insertMathExpression('\\sum_{r=0}^n (-1)^r \\binom{n}{r} = 0') },
        { label: 'Pascal triangle', action: () => insertMathExpression('\\binom{n}{r} = \\binom{n-1}{r-1} + \\binom{n-1}{r}') },
        { label: 'Greatest coefficient', action: () => insertMathExpression('\\binom{n}{\\lfloor n/2 \\rfloor}') },
        { label: 'Sum of squares', action: () => insertMathExpression('\\sum_{r=0}^n \\binom{n}{r}^2 = \\binom{2n}{n}') },
        { label: 'Vandermonde', action: () => insertMathExpression('\\sum_{r=0}^k \\binom{m}{r} \\binom{n}{k-r} = \\binom{m+n}{k}') },
        { label: 'Multinomial', action: () => insertMathExpression('\\frac{n!}{n_1! n_2! ... n_k!}') }
      ]
    },
    // Mathematical Induction
    {
      group: 'Induction',
      buttons: [
        { label: 'Base case', action: () => insertMathExpression('P(1)') },
        { label: 'Inductive step', action: () => insertMathExpression('P(k) \\Rightarrow P(k+1)') },
        { label: 'Conclusion', action: () => insertMathExpression('P(n) \\text{ for all } n \\in \\mathbb{N}') },
        { label: 'Strong induction', action: () => insertMathExpression('P(1), P(2), ..., P(k) \\Rightarrow P(k+1)') },
        { label: 'Well-ordering', action: () => insertMathExpression('\\text{Every non-empty subset of } \\mathbb{N} \\text{ has a least element}') },
        { label: 'Recursive', action: () => insertMathExpression('a_{n+1} = f(a_n)') },
        { label: 'Fibonacci', action: () => insertMathExpression('F_n = F_{n-1} + F_{n-2}') },
        { label: 'Tower of Hanoi', action: () => insertMathExpression('T_n = 2^n - 1') },
        { label: 'Sum of squares', action: () => insertMathExpression('\\sum_{k=1}^n k^2 = \\frac{n(n+1)(2n+1)}{6}') },
        { label: 'Sum of cubes', action: () => insertMathExpression('\\sum_{k=1}^n k^3 = \\left(\\frac{n(n+1)}{2}\\right)^2') }
      ]
    },
    // Tables
    {
      group: 'Tables',
      buttons: [
        { label: 'Table 2×2', action: () => insertTable(2, 2) },
        { label: 'Table 3×3', action: () => insertTable(3, 3) },
        { label: 'Table 4×4', action: () => insertTable(4, 4) },
        { label: 'Table 5×5', action: () => insertTable(5, 5) }
      ]
    },
    // Sets and logic
    {
      group: 'Sets',
      buttons: [
        { label: '∈', action: () => insertMathExpression('\\in') },
        { label: '∉', action: () => insertMathExpression('\\notin') },
        { label: '⊂', action: () => insertMathExpression('\\subset') },
        { label: '⊃', action: () => insertMathExpression('\\supset') },
        { label: '⊆', action: () => insertMathExpression('\\subseteq') },
        { label: '⊇', action: () => insertMathExpression('\\supseteq') },
        { label: '∪', action: () => insertMathExpression('\\cup') },
        { label: '∩', action: () => insertMathExpression('\\cap') },
        { label: '∅', action: () => insertMathExpression('\\emptyset') },
        { label: '∀', action: () => insertMathExpression('\\forall') },
        { label: '∃', action: () => insertMathExpression('\\exists') },
        { label: '¬', action: () => insertMathExpression('\\neg') },
        { label: '∧', action: () => insertMathExpression('\\land') },
        { label: '∨', action: () => insertMathExpression('\\lor') },
        { label: '→', action: () => insertMathExpression('\\rightarrow') },
        { label: '↔', action: () => insertMathExpression('\\leftrightarrow') },
        { label: 'P(n,r)', action: () => insertMathExpression('P(n,r) = \\frac{n!}{(n-r)!}') },
        { label: 'C(n,r)', action: () => insertMathExpression('C(n,r) = \\binom{n}{r} = \\frac{n!}{r!(n-r)!}') },
        { label: 'n!', action: () => insertMathExpression('n!') },
        { label: 'nPr', action: () => insertMathExpression('_nP_r') },
        { label: 'nCr', action: () => insertMathExpression('_nC_r') },
        { label: 'Binomial', action: () => insertMathExpression('\\binom{n}{r}') }
      ]
    }
  ];

  const FractionEditor = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-80">
        <h3 className="text-lg font-semibold mb-4">Create Fraction</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Numerator</label>
            <input
              type="text"
              id="numerator"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter numerator"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Denominator</label>
            <input
              type="text"
              id="denominator"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter denominator"
            />
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => {
                const numerator = document.getElementById('numerator').value;
                const denominator = document.getElementById('denominator').value;
                handleFractionSubmit(numerator, denominator);
              }}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Insert
            </button>
            <button
              onClick={() => setShowFractionEditor(false)}
              className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const SymbolPalette = () => (
    <div className="w-full bg-white border border-gray-300 rounded-lg shadow-lg p-4 max-h-[60vh] overflow-y-auto relative" style={{ zIndex: 10 }}>
      <div className="space-y-6">
        {toolbarButtons.map((group, groupIndex) => (
          <div key={groupIndex} className="mb-6">
            <h4 className="font-semibold text-base text-gray-800 mb-3 border-b-2 border-gray-300 pb-2">{group.group}</h4>
            <div className="grid grid-cols-8 gap-3">
              {group.buttons.map((button, buttonIndex) => (
                <button
                  key={buttonIndex}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    button.action();
                  }}
                  className="px-3 py-2 text-center border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 text-sm font-mono min-h-[40px] flex items-center justify-center transition-colors cursor-pointer relative z-10"
                  title={button.label}
                  style={{ pointerEvents: 'auto' }}
                >
                  <span className="text-xs leading-tight pointer-events-none">{button.icon || button.label}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const TrigHelper = () => (
    <div className="absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-4 z-40 w-64">
      <h4 className="font-semibold text-sm text-gray-700 mb-2">Trigonometry Functions</h4>
      <div className="grid grid-cols-3 gap-2">
        {trigFunctions.map((func, index) => (
          <button
            key={index}
            onClick={() => insertTrigFunction(func.latex)}
            className="p-2 text-center border border-gray-200 rounded hover:bg-gray-100 text-sm"
          >
            {func.name}
          </button>
        ))}
      </div>
    </div>
  );

  const DiagramEditor = () => (
    <div className="absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-4 z-40 w-80">
      <h4 className="font-semibold text-sm text-gray-700 mb-2">Geometry & Diagrams</h4>
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Upload Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="w-full text-sm"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => insertAtCursor('\\triangle ABC')}
            className="p-2 text-center border border-gray-200 rounded hover:bg-gray-100 text-sm flex items-center justify-center"
          >
            <Triangle size={16} className="mr-1" />
            Triangle
          </button>
          <button
            onClick={() => insertAtCursor('\\circle{radius}')}
            className="p-2 text-center border border-gray-200 rounded hover:bg-gray-100 text-sm flex items-center justify-center"
          >
            <Circle size={16} className="mr-1" />
            Circle
          </button>
          <button
            onClick={() => insertAtCursor('\\square ABCD')}
            className="p-2 text-center border border-gray-200 rounded hover:bg-gray-100 text-sm flex items-center justify-center"
          >
            <SquareIcon size={16} className="mr-1" />
            Square
          </button>
          <button
            onClick={() => insertAtCursor('\\angle ABC')}
            className="p-2 text-center border border-gray-200 rounded hover:bg-gray-100 text-sm flex items-center justify-center"
          >
            ∠
          </button>
        </div>
      </div>
    </div>
  );

  const CommonExpressions = () => (
    <div className="absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-4 z-40 w-96 max-h-64 overflow-y-auto">
      <h4 className="font-semibold text-sm text-gray-700 mb-2">Common Expressions</h4>
      <div className="space-y-2">
        {commonExpressions.map((expr, index) => (
          <button
            key={index}
            onClick={() => insertExpression(expr.latex)}
            className="w-full p-2 text-left border border-gray-200 rounded hover:bg-gray-100 text-sm"
            title={expr.latex}
          >
            <div className="font-medium">{expr.name}</div>
            <div className="text-xs text-gray-500 truncate">{expr.latex}</div>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className={`relative ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 mb-3 p-3 bg-gray-50 rounded-lg border">
        {/* Toggle Button */}
        <button
          onClick={() => {
            setShowSymbols(!showSymbols);
            if (onToggleCollapse) onToggleCollapse(!showSymbols);
          }}
          className="px-3 py-1 rounded-md text-sm flex items-center bg-blue-600 text-white hover:bg-blue-700"
        >
          <Calculator size={16} className="mr-1" />
          {showSymbols ? 'Hide Math' : 'Show Math'}
          {showSymbols ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
        </button>

        {showSymbols && (
          <>
            {/* Symbol Palette */}
            <div className="relative">
              <button
                onClick={() => setShowSymbols(!showSymbols)}
                className="px-3 py-1 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 text-sm flex items-center"
              >
                <Type size={16} className="mr-1" />
                Symbols
              </button>
            </div>

            {/* Fraction Editor */}
            <div className="relative">
              <button
                onClick={() => setShowFractionEditor(!showFractionEditor)}
                className="px-3 py-1 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 text-sm flex items-center"
              >
                <Divide size={16} className="mr-1" />
                Fraction
              </button>
              {showFractionEditor && <FractionEditor />}
            </div>

            {/* Trigonometry Helper */}
            <div className="relative">
              <button
                onClick={() => setShowTrigHelper(!showTrigHelper)}
                className="px-3 py-1 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 text-sm flex items-center"
              >
                <Calculator size={16} className="mr-1" />
                Trig
              </button>
              {showTrigHelper && <TrigHelper />}
            </div>

            {/* Diagram Editor */}
            <div className="relative">
              <button
                onClick={() => setShowDiagramEditor(!showDiagramEditor)}
                className="px-3 py-1 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 text-sm flex items-center"
              >
                <ImageIcon size={16} className="mr-1" />
                Diagrams
              </button>
              {showDiagramEditor && <DiagramEditor />}
            </div>

            {/* Common Expressions */}
            <div className="relative">
              <button
                onClick={() => setShowTemplates(!showTemplates)}
                className="px-3 py-1 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 text-sm flex items-center"
              >
                <FileText size={16} className="mr-1" />
                Templates
              </button>
              {showTemplates && <CommonExpressions />}
            </div>
          </>
        )}

        {/* Symbol Palette - appears below main buttons */}
        {showSymbols && (
          <div className="mt-4">
            <SymbolPalette />
          </div>
        )}
      </div>

      {/* Close dropdowns when clicking outside - but not for symbol palette */}
      {(showFractionEditor || showTrigHelper || showDiagramEditor || showTemplates) && (
        <div 
          className="fixed inset-0 z-30" 
          onClick={() => {
            setShowFractionEditor(false);
            setShowTrigHelper(false);
            setShowDiagramEditor(false);
            setShowTemplates(false);
          }}
        />
      )}
    </div>
  );
};

export default MathToolbar;
